import { appBlock } from '@/containers/agents/agent-enforcement.util';
import { formatAgentCredits } from '@/containers/agents/agent-prices';
import type { AgentDto } from '@/types/gtm-agents';
import type {
  AgentSelfConfig,
  AgentSelfTool,
  NodeCost,
  ProfileReadsCost,
  ResearchConfig,
  SelfCost,
  ValidateGraphResponse,
} from '@/types/custom-playbooks';

/**
 * The agent-instructions skill option of the Agents step (W2-48): `"self"`
 * in `agentIds` runs the play's own agent on each record. Mirrors the API's
 * `utils/play_agent_step.py` and `providers/agents/tool_policy.py`; the
 * server validates and prices it, these only draw it. Its price is the
 * agent formula on the step's task and outputs plus each web tool call, at
 * the org's agent price: the portal reads it from the validate response and
 * never works it out.
 */
export const SELF_AGENT_ID = 'self';
export const SELF_LABEL = 'Agent instructions';
export const SELF_MAX_TOOL_CALLS = 6;
export const SELF_DEFAULT_TOOL_CALLS = 3;

export const SELF_TOOLS: { id: AgentSelfTool; label: string }[] = [
  { id: 'web_search', label: 'Web search' },
  { id: 'fetch_url', label: 'Read web pages' },
  { id: 'crm_record', label: 'CRM record' },
  { id: 'knowledge', label: 'Knowledge' },
  { id: 'linkedin_profile', label: 'LinkedIn profiles' },
];

const SHORT_TOOL: Record<AgentSelfTool, string> = {
  web_search: 'Web',
  fetch_url: 'Pages',
  crm_record: 'CRM',
  knowledge: 'Knowledge',
  linkedin_profile: 'LinkedIn',
};

export function hasSelf(config: Pick<ResearchConfig, 'agentIds'>): boolean {
  return (config.agentIds ?? []).includes(SELF_AGENT_ID);
}

export function emptySelf(): AgentSelfConfig {
  return {
    task: '',
    outputs: null,
    tools: [],
    maxToolCalls: SELF_DEFAULT_TOOL_CALLS,
  };
}

/**
 * The option's price for the step `nodeId` from the validate response
 * (`costs.<node>.self`), or undefined until the validation has one: it
 * follows the step's task, outputs and tools, so a price that isn't current
 * is hidden instead of guessed.
 */
export function selfCost(
  response: Pick<ValidateGraphResponse, 'costs'> | undefined,
  nodeId: string
): SelfCost | undefined {
  return response?.costs?.[nodeId]?.self;
}

/** A quote that includes web calls is the most a record costs: it is
 *  charged only for the calls made, so its words are "up to". */
export function selfPricePrefix(cost: SelfCost): string {
  return cost.maxWebCalls > 0 ? 'up to ' : '';
}

/** The agent-instructions option is on and lists the LinkedIn profiles tool. */
export function hasProfileTool(
  config: Pick<ResearchConfig, 'agentIds' | 'self'>
): boolean {
  return (
    hasSelf(config) && (config.self?.tools ?? []).includes('linkedin_profile')
  );
}

/** What the profile reads cost, in the server's words: "plus 1 credit per
 *  LinkedIn profile copy read, up to 4 a record". Every number is the
 *  server's (`costs[id].profileReads`). */
export function profileReadsWords(reads: ProfileReadsCost): string {
  const credits =
    reads.perRead === 1
      ? '1 credit'
      : `${formatAgentCredits(reads.perRead)} credits`;
  return `plus ${credits} per LinkedIn profile copy read, up to ${reads.maxPerRecord} a record`;
}

/** The agent-instructions option's price a record, all of it the server's:
 *  its own price (`self`: the agent formula plus its web calls) and what its
 *  LinkedIn profile reads add at most (`reads`). */
export interface SelfPrice {
  self?: SelfCost;
  reads?: ProfileReadsCost;
}

/**
 * The option's price from the step's cost in the validation of the current
 * graph (`cost`), or nothing yet: every number is the server's, so a price
 * that isn't current is hidden instead of guessed. A step the server says
 * reads nothing (no LinkedIn profiles tool, or company records) has no reads.
 */
export function selfPrice(cost: NodeCost | undefined): SelfPrice {
  if (!cost?.self) return {};
  return cost.profileReads
    ? { self: cost.self, reads: cost.profileReads }
    : { self: cost.self };
}

/** The step card's words: "Agent instructions · 3 outputs · Web". */
export function selfSummary(block: AgentSelfConfig | undefined): string {
  const count = block?.outputs?.fields?.length ?? 0;
  const parts = [SELF_LABEL, `${count} output${count === 1 ? '' : 's'}`];
  const tools = SELF_TOOLS.filter((tool) =>
    (block?.tools ?? []).includes(tool.id)
  ).map((tool) => SHORT_TOOL[tool.id]);
  if (tools.length) parts.push(tools.join(', '));
  return parts.join(' · ');
}

/** The Agents step's price a record: the other agents from `perAgent`, plus
 *  the option's price from the backend (`selfPerRecord`). */
export function researchCreditsPerRecord(
  config: ResearchConfig,
  perAgent: (ids: readonly string[]) => number,
  selfPerRecord?: number
): number {
  const others = (config.agentIds ?? []).filter((id) => id !== SELF_AGENT_ID);
  return perAgent(others) + (hasSelf(config) ? (selfPerRecord ?? 0) : 0);
}

const CRM_APPS = ['salesforce', 'hubspot', 'attio'] as const;
const NONE: ReadonlySet<string> = new Set();

/**
 * Whether the agent refuses a tool today; the run leaves a refused tool out.
 * The play's validate route says it for the tools the step lists
 * (`refused`: `research_self_tool_off`, from the server's one decision). For
 * the others, which a step can't be asked about before they are picked: web
 * search by its switch, LinkedIn profiles by its Enrich switch, CRM record by
 * the API's answer for each CRM's Read records (`appBlocks`), and knowledge
 * with both the profile and the company notes off.
 */
export function selfToolOff(
  agent: Pick<AgentDto, 'tools' | 'appBlocks' | 'knowledge'> | undefined,
  tool: AgentSelfTool,
  refused: ReadonlySet<string> = NONE
): boolean {
  if (refused.has(tool)) return true;
  if (!agent) return false;
  if (tool === 'web_search' || tool === 'fetch_url')
    return agent.tools?.web_search === false;
  if (tool === 'linkedin_profile') return agent.tools?.enrich === false;
  if (tool === 'crm_record')
    return CRM_APPS.some((app) => appBlock(agent, app, 'crm_read'));
  return (
    agent.knowledge?.profile === false &&
    agent.knowledge?.companyNotes === false
  );
}

/** The config after the picker changed `agentIds`: the agent-instructions
 *  option keeps its settings while picked and drops them when unpicked. */
export function withAgentIds(
  config: ResearchConfig,
  agentIds: string[]
): ResearchConfig {
  const { self, ...rest } = config;
  if (!agentIds.includes(SELF_AGENT_ID)) return { ...rest, agentIds };
  return { ...rest, agentIds, self: self ?? emptySelf() };
}
